import { createBrowserRouter } from 'react-router-dom';

import {
  AuthBoot,
  EmailGate,
  OnboardingGate,
  RequireAuth,
  RequireMember,
  RequirePermission,
} from '@/auth/guards';
import { AppShell } from '@/layout/AppShell';
import { Permission } from '@/models/permissions';

import { prefetchCalendarOnBoot, preloadCalendarChunk } from './bootPrefetch';
import { lazyEl as el, lazyWithRetry } from './lazyRoute';
import { RootRouteError } from './RootRouteError';

const Login = lazyWithRetry(() => import('@/screens/auth/LoginScreen'));
const Onboarding = lazyWithRetry(() => import('@/screens/auth/OnboardingScreen'));
const NewPassword = lazyWithRetry(() => import('@/screens/auth/NewPasswordScreen'));
const Consent = lazyWithRetry(() => import('@/screens/auth/ConsentScreen'));
const MagicLogin = lazyWithRetry(() => import('@/screens/auth/MagicLoginScreen'));
const Home = lazyWithRetry(() => import('@/screens/public/HomeScreen'));
const Faq = lazyWithRetry(() => import('@/screens/public/FaqScreen'));
const Donate = lazyWithRetry(() => import('@/screens/public/DonateScreen'));
const Install = lazyWithRetry(() => import('@/screens/public/InstallAppScreen'));
const SmsPolicy = lazyWithRetry(() => import('@/screens/public/SmsPolicyScreen'));
const Guidelines = lazyWithRetry(() => import('@/screens/public/GuidelinesScreen'));
const Volunteer = lazyWithRetry(() => import('@/screens/public/VolunteerScreen'));
const Join = lazyWithRetry(() => import('@/screens/public/JoinScreen'));
const JoinSuccess = lazyWithRetry(() => import('@/screens/public/JoinSuccessScreen'));
const loadCalendar = () => import('@/screens/calendar/CalendarScreen');
const Calendar = lazyWithRetry(loadCalendar);
preloadCalendarChunk(loadCalendar);
const EventDetail = lazyWithRetry(() => import('@/screens/events/EventDetailScreen'));
const EventCreate = lazyWithRetry(() => import('@/screens/events/EventCreateScreen'));
const EventEdit = lazyWithRetry(() => import('@/screens/events/EventEditScreen'));
const EventAttendance = lazyWithRetry(() => import('@/screens/events/EventAttendanceScreen'));
const EventManageRsvps = lazyWithRetry(() => import('@/screens/events/EventManageRsvpsScreen'));
const EventQuestionResponses = lazyWithRetry(
  () => import('@/screens/events/EventQuestionResponsesScreen'),
);
const EventCheckInReport = lazyWithRetry(() => import('@/screens/events/EventCheckInReportScreen'));
const EventFeedback = lazyWithRetry(() => import('@/screens/events/EventFeedbackScreen'));
const MyEvents = lazyWithRetry(() => import('@/screens/events/MyEventsScreen'));
const PublicRsvps = lazyWithRetry(() => import('@/screens/events/PublicRsvpsScreen'));
const Notifications = lazyWithRetry(() => import('@/screens/notifications/NotificationsScreen'));
const Profile = lazyWithRetry(() => import('@/screens/profile/ProfileScreen'));
const Settings = lazyWithRetry(() => import('@/screens/settings/SettingsScreen'));
const Docs = lazyWithRetry(() => import('@/screens/docs/DocsScreen'));
const DocDetail = lazyWithRetry(() => import('@/screens/docs/DocDetailScreen'));
const Survey = lazyWithRetry(() => import('@/screens/surveys/SurveyScreen'));
const SurveyThanks = lazyWithRetry(() => import('@/screens/surveys/SurveyThanksScreen'));
const Feedback = lazyWithRetry(() => import('@/screens/feedback/FeedbackScreen'));
const ConnectAuthorize = lazyWithRetry(() => import('@/screens/connect/ConnectAuthorizeScreen'));

// Admin screens
const AdminHub = lazyWithRetry(() => import('@/screens/admin/AdminHubScreen'));
const JoinRequestsAdmin = lazyWithRetry(() => import('@/screens/admin/JoinRequestsScreen'));
const EventManagement = lazyWithRetry(() => import('@/screens/admin/EventManagementScreen'));
const FlaggedEvents = lazyWithRetry(() => import('@/screens/admin/FlaggedEventsScreen'));
const AttendanceReport = lazyWithRetry(() => import('@/screens/admin/AttendanceReportScreen'));
const JoinFormAdmin = lazyWithRetry(() => import('@/screens/admin/JoinFormAdminScreen'));
const SurveyAdminList = lazyWithRetry(() => import('@/screens/admin/SurveyAdminListScreen'));
const SurveyBuilder = lazyWithRetry(() => import('@/screens/admin/SurveyBuilderScreen'));
const SurveyCreate = lazyWithRetry(() => import('@/screens/admin/SurveyCreateScreen'));
const SurveyResponses = lazyWithRetry(() => import('@/screens/admin/SurveyResponsesScreen'));
const SurveyPreview = lazyWithRetry(() => import('@/screens/admin/SurveyPreviewScreen'));
const Members = lazyWithRetry(() => import('@/screens/admin/MembersScreen'));
const MemberDetail = lazyWithRetry(() => import('@/screens/admin/MemberDetailScreen'));
const MemberProfile = lazyWithRetry(() => import('@/screens/members/MemberProfileScreen'));
const MembersDirectory = lazyWithRetry(() => import('@/screens/members/MembersDirectoryScreen'));

const Stub = lazyWithRetry(() => import('@/screens/NotImplemented'));

export const router = createBrowserRouter([
  {
    element: (
      <AuthBoot onSessionToken={prefetchCalendarOnBoot}>
        <OnboardingGate />
      </AuthBoot>
    ),
    errorElement: <RootRouteError />,
    children: [
      {
        element: <EmailGate />,
        children: [
          // Auth screens render edge-to-edge (no AppShell).
          { path: '/login', element: el(<Login />) },
          { path: '/magic-login/:token', element: el(<MagicLogin />) },
          { path: '/onboarding', element: el(<Onboarding />) },
          { path: '/new-password', element: el(<NewPassword />) },
          { path: '/consent', element: el(<Consent />) },
          {
            element: <RequireAuth />,
            children: [{ path: '/connect/authorize', element: el(<ConnectAuthorize />) }],
          },

          // Everything else uses the shared shell (nav + outlet).
          {
            element: <AppShell />,
            children: [
              // ---- public ----
              { path: '/', element: el(<Home />) },
              { path: '/join', element: el(<Join />) },
              { path: '/join/success', element: el(<JoinSuccess />) },
              { path: '/calendar', element: el(<Calendar />) },
              { path: '/events/:id', element: el(<EventDetail />) },
              { path: '/my-rsvps', element: el(<PublicRsvps />) },
              { path: '/surveys/:slug', element: el(<Survey />) },
              { path: '/surveys/:slug/thanks', element: el(<SurveyThanks />) },
              { path: '/feedback', element: el(<Feedback />) },
              { path: '/donate', element: el(<Donate />) },
              { path: '/install', element: el(<Install />) },
              { path: '/faq', element: el(<Faq />) },
              { path: '/sms-policy', element: el(<SmsPolicy />) },
              // Public so join-form applicants can read what they agree to.
              { path: '/guidelines', element: el(<Guidelines />) },

              // ---- authed ----
              {
                element: <RequireAuth />,
                children: [
                  { path: '/settings', element: el(<Settings />) },
                  { path: '/notifications', element: el(<Notifications />) },
                  { path: '/profile', element: el(<Profile />) },
                  { path: '/volunteer', element: el(<Volunteer />) },
                  { path: '/events/mine', element: el(<MyEvents />) },
                  { path: '/events/:id/edit', element: el(<EventEdit />) },
                  { path: '/events/:id/check-in', element: el(<EventAttendance />) },
                  { path: '/events/:id/manage-rsvps', element: el(<EventManageRsvps />) },
                  { path: '/events/:id/responses', element: el(<EventQuestionResponses />) },
                ],
              },

              {
                element: <RequireMember unlocks="add events" />,
                children: [{ path: '/events/add', element: el(<EventCreate />) }],
              },

              {
                element: <RequireMember unlocks="see the member list" />,
                children: [
                  { path: '/members', element: el(<MembersDirectory />) },
                  { path: '/members/:userId', element: el(<MemberProfile />) },
                ],
              },

              {
                element: <RequireAuth />,
                children: [
                  { path: '/events/:id/report', element: el(<EventCheckInReport />) },
                  { path: '/events/:id/feedback', element: el(<EventFeedback />) },
                ],
              },

              // ---- admin hub: any authed user can visit; the hub itself shows
              //      only the tiles their permissions allow.
              {
                element: <RequireAuth />,
                children: [{ path: '/admin', element: el(<AdminHub />) }],
              },

              // ---- permissioned ----
              {
                element: <RequirePermission perm={Permission.ManageUsers} />,
                children: [
                  { path: '/admin/members', element: el(<Members />) },
                  { path: '/admin/members/:id', element: el(<MemberDetail />) },
                ],
              },
              {
                element: <RequirePermission perm={Permission.ApproveJoinRequests} />,
                children: [{ path: '/join-requests', element: el(<JoinRequestsAdmin />) }],
              },
              {
                element: <RequirePermission perm={Permission.ManageEvents} />,
                children: [
                  { path: '/events/manage', element: el(<EventManagement />) },
                  { path: '/admin/flagged-events', element: el(<FlaggedEvents />) },
                  { path: '/admin/attendance', element: el(<AttendanceReport />) },
                ],
              },
              {
                element: <RequirePermission perm={Permission.ManageDocuments} />,
                children: [
                  { path: '/docs', element: el(<Docs />) },
                  { path: '/docs/:id', element: el(<DocDetail />) },
                ],
              },
              {
                element: <RequirePermission perm={Permission.EditJoinQuestions} />,
                children: [{ path: '/admin/join-form', element: el(<JoinFormAdmin />) }],
              },
              {
                element: <RequirePermission perm={Permission.ManageSurveys} />,
                children: [
                  { path: '/admin/surveys', element: el(<SurveyAdminList />) },
                  { path: '/admin/surveys/new', element: el(<SurveyCreate />) },
                  { path: '/admin/surveys/:id', element: el(<SurveyBuilder />) },
                  { path: '/admin/surveys/:id/responses', element: el(<SurveyResponses />) },
                  { path: '/admin/surveys/:id/preview', element: el(<SurveyPreview />) },
                ],
              },

              // ---- catch-all ----
              { path: '*', element: el(<Stub />) },
            ],
          },
        ],
      },
    ],
  },
]);
